package com.fireavert.units.list_unit.client.presentation

import com.fireavert.cache.client.Preferences
import com.fireavert.common.TableColumn
import com.fireavert.common.TableColumn.SortOrder.Neutral
import com.fireavert.common.Try
import com.fireavert.devices.data_models.data_model.DeviceType
import com.fireavert.devices.data_models.data_model.DeviceType.*
import com.fireavert.events.active_events.client.ActiveEventsRepository
import com.fireavert.events.shared.data_models.models.EventType
import com.fireavert.fire_risk.client.ClientTenantFireRiskRepository
import com.fireavert.fire_risk.data_models.TenantFireRiskDataModel
import com.fireavert.logging.Logger
import com.fireavert.units.shared.client.UnitsNavigator
import com.fireavert.units.shared.client.UnitsRepository
import com.fireavert.units.shared.data_models.UnitModel
import com.fireavert.user.data_models.Permission

class ClientUnitsInteractor(
    private val screen: UnitsSectionScreen,
    private val preferences: Preferences,
    private val unitsRepository: UnitsRepository,
    private val unitsNavigator: UnitsNavigator,
    private val activeEventsRepository: ActiveEventsRepository,
    private val tenantFireRiskRepository: ClientTenantFireRiskRepository,
    private val logger: Logger
) : UnitsInteractor {
    override suspend fun onLoad(propertyId: Int) {
        screen.showAddUnit = preferences.permissions.contains(Permission.CreateUnits)
        screen.showEditUnit = preferences.permissions.contains(Permission.EditUnits)
        screen.showDeleteUnit = preferences.permissions.contains(Permission.DeleteUnits)
        screen.loading = true
        val unitModels = when (val maybeUnitModels = getUnitModels(propertyId)) {
            is Try.Error -> {
                screen.loading = false
                logger.d("Failed to get units for property id $propertyId.")
                null
            }
            is Try.Success -> {
                maybeUnitModels.value
            }
        } ?: return
        val fireRiskLevels = getFireRiskLevels(propertyId)
        unitModels.forEach { unit ->
            val riskData = fireRiskLevels.find { it.unitId == unit.id }
            riskData?.let { unit.fireRiskTenantData = riskData }
        }
        screen.unitModels = unitModels
        screen.loading = false
    }

    override fun showAddNewUnit(propertyId: Int) {
        unitsNavigator.navigate(UnitsNavigator.Destination.AddNewUnit(propertyId = propertyId))
    }

    override fun stringOutputForCount(count: Int): String {
        return if (count == -1) "-" else count.toString()
    }

    override fun clearState() {
        screen.unitModels = emptyList()
    }

    override fun searchAndsortWithColumns(
        columns: Array<TableColumn>,
        unitModels: Array<UnitModel>,
        searchValue: String
    ): Array<UnitModel> {
        val filteredUnits = searchUnits(searchValue, unitModels)
        val sortColumn = columns.firstOrNull { it.sortOrder != Neutral } ?: return filteredUnits

        return when (sortColumn.text) {
            "UNIT" -> {
                TableColumn.sortList(true, sortColumn, filteredUnits.toList()) { it.number }
                    .toTypedArray()
            }
            "CONNECTION STATUS" -> {
                TableColumn.sortList(true, sortColumn, filteredUnits.toList()) { it.status }
                    .toTypedArray()
            }
            "FIRE RISK LEVEL" -> {
                TableColumn.sortList(false, sortColumn, filteredUnits.toList()) { it.fireRiskTenantData.riskRating }
                    .toTypedArray()
            }
            "LEAK EVENTS" -> {
                TableColumn.sortList(false, sortColumn, filteredUnits.toList()) { it.leakEventCount }
                    .toTypedArray()
            }
            "TAMPER EVENTS" -> {
                TableColumn.sortList(false, sortColumn, filteredUnits.toList()) { it.tamperEventCount }
                    .toTypedArray()
            }
            else -> {
                filteredUnits
            }
        }
    }

    override fun searchAndSortWithColumnsMobile(
        columns: Array<TableColumn>,
        unitModels: Array<UnitModel>,
        searchValue: String
    ): Array<UnitModel> {
        val filteredUnits = searchUnits(searchValue, unitModels)
        val sortColumn = columns.firstOrNull { it.sortOrder != Neutral } ?: return filteredUnits

        return when (sortColumn.text) {
            "Units" -> {
                TableColumn.sortList(true, sortColumn, filteredUnits.toList()) { it.number }
                    .toTypedArray()
            }
            "Smoke" -> {
                TableColumn.sortList(true, sortColumn, filteredUnits.toList()) { it.status }
                    .toTypedArray()
            }
            "Stove" -> {
                TableColumn.sortList(false, sortColumn, filteredUnits.toList()) { it.alarmEventCount }
                    .toTypedArray()
            }
            "Leak" -> {
                TableColumn.sortList(
                    false,
                    sortColumn,
                    filteredUnits.toList()
                ) { it.shutoffEventCount }.toTypedArray()
            }
            "Tamper" -> {
                TableColumn.sortList(false, sortColumn, filteredUnits.toList()) { it.leakEventCount }
                    .toTypedArray()
            }
            else -> {
                filteredUnits
            }
        }
    }

    private fun searchUnits(searchValue: String, unitModels: Array<UnitModel>): Array<UnitModel> {
        return unitModels.filter { unit ->
            unit.number.contains(searchValue, ignoreCase = true)
        }.toTypedArray()
    }

    override fun showAddNewUnitCSV(propertyId: Int) {
        unitsNavigator.navigate(UnitsNavigator.Destination.AddNewUnitCSV(propertyId = propertyId))
    }

    override fun clickedCommV2Conversion(propertyId: Int) {
        unitsNavigator.navigate(UnitsNavigator.Destination.ConvertToCommV2(propertyId = propertyId))
    }

    override suspend fun exportUnitData(propertyId: Int, unitModels: List<UnitModel>): String? {
        val dataList = when (val maybe = activeEventsRepository.getActiveEventsForProperty(propertyId)) {
            is Try.Error -> {
                logger.d("Failed to get export unit data for property id $propertyId.")
                return null
            }
            is Try.Success -> {
                maybe.value
            }
        }

        val activeEventsMap = dataList.activeDeviceEvents.associateBy { it.unitId }

        val csvContent = buildString {
            appendLine("Unit Number,Status,Smoke Events,Stove Shutoffs,Leak Events,Tamper Events, Active Event Happening?, Active Event Device, Active Event Type, Active Event Timestamp")
            unitModels
                .forEach { unitModel ->
                    appendLine(
                        "${unitModel.number},${unitModel.status},${
                            checkForNegativeValues(
                                unitModel.alarmEventCount
                            )
                        }," +
                                "${checkForNegativeValues(unitModel.shutoffEventCount)},${
                                    checkForNegativeValues(
                                        unitModel.leakEventCount
                                    )
                                }," +
                                checkForNegativeValues(unitModel.tamperEventCount)
                                + if (activeEventsMap.containsKey(unitModel.id)) {
                            ",Yes,${activeEventsMap[unitModel.id]?.deviceType},${convertEventTypeToString(activeEventsMap[unitModel.id]?.eventType, activeEventsMap[unitModel.id]?.deviceType)},${activeEventsMap[unitModel.id]?.timestamp}"
                        } else {
                            ",No, , , ,"
                        }
                    )
                }
        }
        return csvContent
    }

    private suspend fun getUnitModels(propertyId: Int): Try<List<UnitModel>> {
        return unitsRepository.getUnitsForPropertyId(propertyId)
    }

    private suspend fun getUnitRiskLevel(propertyId: Int): Try<List<TenantFireRiskDataModel>> {
        return tenantFireRiskRepository.getTenantFireRiskAllProperties(propertyId)
    }

    private suspend fun getFireRiskLevels(propertyId: Int): List<TenantFireRiskDataModel> {
        return when (val maybeFireModel = getUnitRiskLevel(propertyId)) {
            is Try.Error -> {
                logger.d("Failed to get units for property id $propertyId.")
                null
            }

            is Try.Success -> {
                maybeFireModel.value
            }
        } ?: return emptyList()
    }


    private fun checkForNegativeValues(value: Int): String {
        return if (value < 0) {
            "N/A"
        } else {
            value.toString()
        }
    }

    private fun convertEventTypeToString(eventType: EventType?, deviceType: DeviceType?): String {
        if (eventType == EventType.DeviceTriggered) {
            return when (deviceType) {
                FireAvert, FireAvertGas, FireAvertAppliance -> "Stove Shutoff"
                WaterSensor -> "Water Leak"
                FlowSensor -> "Flow"
                TamperSensor -> "Tamper Detected"
                null -> "Device Triggered"
            }
        }
        return "Device Triggered"
    }
}